import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { IconRail } from '@/features/reader/IconRail';
import { ApiError } from '@/lib/http';
import { fetchLanguages, type Language } from '@/lib/languages';
import {
  agreeTerm,
  fetchAdherence,
  fetchGlossary,
  type GlossaryEntry,
} from './glossaryApi';

/**
 * The glossary: one agreed rendering per term, per language.
 *
 * It is what keeps the product consistent across time. If تَقْوَى is
 * "God-consciousness" one Friday and "piety" the next, a reader who is
 * learning cannot build on either.
 *
 * **It never rewrites anything.** Agreeing a term changes no existing
 * translation; the adherence tab shows where they differ and a person
 * decides. That is said on the screen, because it is the thing an editor is
 * most likely to assume otherwise.
 */
export function GlossaryScreen() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'terms' | 'adherence'>('terms');
  const [language, setLanguage] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const languages = useQuery({
    queryKey: ['languages', 'target'],
    queryFn: ({ signal }) => fetchLanguages('target', signal),
    staleTime: 60 * 60 * 1000,
  });

  const glossary = useQuery({
    queryKey: ['glossary', language],
    queryFn: ({ signal }) => fetchGlossary(language, signal),
  });

  const adherence = useQuery({
    queryKey: ['glossary', 'adherence', language],
    queryFn: ({ signal }) => fetchAdherence(language, signal),
    enabled: tab === 'adherence',
  });

  // Grouped by term so every language's decision sits on one row — four
  // scripts side by side is the layout problem this screen has.
  const terms = useMemo(() => {
    const byTerm = new Map<string, GlossaryEntry[]>();
    for (const entry of glossary.data ?? []) {
      const existing = byTerm.get(entry.termSource);
      if (existing) existing.push(entry);
      else byTerm.set(entry.termSource, [entry]);
    }
    return [...byTerm.entries()];
  }, [glossary.data]);

  const agree = useMutation({
    retry: false,
    mutationFn: agreeTerm,
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ['glossary'] });
    },
  });

  return (
    <div className="flex min-h-full bg-background text-foreground">
      <IconRail current="admin" />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-b border-border bg-card px-4 py-3 sm:px-6">
          <div className="mx-auto w-full max-w-[1100px]">
            <nav aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li>
                  <a href="?screen=upload" className="hover:underline">
                    Admin review
                  </a>
                </li>
                <li aria-hidden>›</li>
                <li className="font-medium text-foreground" aria-current="page">
                  Glossary
                </li>
              </ol>
            </nav>
            <h1 className="mt-2 text-xl font-semibold">Glossary</h1>
            <p className="mt-1 max-w-2xl text-sm text-pretty text-muted-foreground">
              One agreed rendering per term, so the same word reads the same way
              every week. <b>Agreeing a term changes nothing already
              translated</b> — the adherence tab shows where they differ.
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div role="tablist" className="flex gap-1">
                {(['terms', 'adherence'] as const).map((id) => (
                  <button
                    key={id}
                    role="tab"
                    aria-selected={tab === id}
                    onClick={() => setTab(id)}
                    className={cn(
                      'min-h-9 rounded-md px-3 text-sm',
                      tab === id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {id === 'terms' ? 'Terms' : 'Adherence'}
                  </button>
                ))}
              </div>

              <label className="ms-auto flex items-center gap-1.5 text-xs">
                <span className="text-muted-foreground">Language</span>
                <select
                  value={language ?? ''}
                  onChange={(event) => setLanguage(event.target.value || null)}
                  className="h-8 rounded-md border border-border bg-background px-2 text-[13px]"
                >
                  <option value="">All</option>
                  {(languages.data ?? []).map((entry) => (
                    <option key={entry.code} value={entry.code}>
                      {entry.nameEn}
                    </option>
                  ))}
                </select>
              </label>

              <Button
                type="button"
                size="lg"
                onClick={() => setEditing(editing === '' ? null : '')}
              >
                Agree a term
              </Button>
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1100px] flex-1 px-4 py-6 sm:px-6">
          {editing !== null ? (
            <TermEditor
              languages={languages.data ?? []}
              initialTerm={editing}
              pending={agree.isPending}
              error={agree.error}
              onCancel={() => setEditing(null)}
              onAgree={(input) => agree.mutate(input)}
            />
          ) : null}

          {agree.isSuccess && editing === null ? (
            <p role="status" className="mb-4 rounded-md bg-success-bg p-3 text-sm text-success">
              {agree.data}
            </p>
          ) : null}

          {tab === 'terms' ? (
            glossary.isPending ? (
              <Skeleton className="h-40" />
            ) : glossary.error ? (
              <Failure error={glossary.error} />
            ) : terms.length === 0 ? (
              <Empty
                title="No terms agreed yet"
                body="This is where it starts. The first term you agree is the first word the product will render the same way every week."
              />
            ) : (
              <ul className="flex flex-col gap-2">
                {terms.map(([term, entries]) => (
                  <li
                    key={term}
                    className="grid gap-3 rounded-lg border border-border bg-card p-4 md:grid-cols-[220px_1fr]"
                  >
                    <div>
                      <p lang="ar" dir="rtl" className="script-ar text-xl font-normal">
                        {term}
                      </p>
                      {entries[0]?.transliteration ? (
                        <p className="mt-0.5 text-xs italic text-muted-foreground">
                          {entries[0].transliteration}
                        </p>
                      ) : null}
                    </div>

                    <dl className="grid gap-2 sm:grid-cols-2">
                      {entries.map((entry) => {
                        const language = (languages.data ?? []).find(
                          (l) => l.code === entry.language,
                        );
                        return (
                          <div key={entry.id} className="flex flex-col">
                            <dt className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
                              {language?.nameEn ?? entry.language}
                            </dt>
                            <dd
                              dir={language?.direction ?? 'ltr'}
                              className={cn(
                                language?.scriptClass ?? 'script-latin',
                                'text-sm',
                              )}
                            >
                              {entry.doNotTranslate ? (
                                <span className="italic text-muted-foreground">
                                  Left in Arabic
                                </span>
                              ) : (
                                entry.preferredRendering
                              )}
                            </dd>
                            {entry.note ? (
                              <dd className="mt-0.5 text-xs text-muted-foreground">
                                {entry.note}
                              </dd>
                            ) : null}
                          </div>
                        );
                      })}
                    </dl>
                  </li>
                ))}
              </ul>
            )
          ) : adherence.isPending ? (
            <Skeleton className="h-40" />
          ) : adherence.error ? (
            <Failure error={adherence.error} />
          ) : (
            <Adherence rows={adherence.data ?? []} />
          )}
        </main>
      </div>
    </div>
  );
}

function Adherence({
  rows,
}: {
  rows: readonly {
    khutbahId: string;
    titleAr: string | null;
    language: string;
    termSource: string;
    preferredRendering: string;
    passages: number;
  }[];
}) {
  return (
    <div className="flex flex-col gap-3">
      {/* A report, not an error list — said before the rows, because a list
          of "disagreements" invites being read as a list of mistakes. */}
      <p className="rounded-md bg-muted p-3 text-[13px] text-pretty">
        Where a translation uses something other than the agreed rendering. A
        translator may have been right to deviate — context beats a table — so
        this names them and stops there. Matching is approximate: there is no
        stemming for these languages, so an inflected form will be missed.
        <b> This is not exhaustive.</b>
      </p>

      {rows.length === 0 ? (
        <Empty
          title="Nothing disagrees"
          body="Every published translation uses the agreed rendering where the term appears — or no terms are agreed yet."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li
              key={`${row.khutbahId}-${row.language}-${row.termSource}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
            >
              <span className="flex min-w-0 flex-col">
                <span lang="ar" dir="rtl" className="script-ar text-base">
                  {row.termSource}
                </span>
                <span className="text-xs text-muted-foreground">
                  agreed as “{row.preferredRendering}” in {row.language} ·{' '}
                  {row.passages} passage{row.passages === 1 ? '' : 's'}
                </span>
              </span>
              <span className="flex items-center gap-2">
                {row.titleAr ? (
                  <span lang="ar" dir="rtl" className="script-ar text-sm">
                    {row.titleAr}
                  </span>
                ) : null}
                <Button type="button" variant="outline" size="sm" asChild>
                  <a href={`?khutbah=${encodeURIComponent(row.khutbahId)}`}>Open</a>
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TermEditor({
  languages,
  initialTerm,
  pending,
  error,
  onCancel,
  onAgree,
}: {
  languages: readonly Language[];
  initialTerm: string;
  pending: boolean;
  error: unknown;
  onCancel: () => void;
  onAgree: (input: {
    language: string;
    termSource: string;
    preferredRendering: string;
    doNotTranslate: boolean;
    transliteration: string;
    note: string;
  }) => void;
}) {
  const [term, setTerm] = useState(initialTerm);
  const [language, setLanguage] = useState(languages[0]?.code ?? 'ml');
  const [rendering, setRendering] = useState('');
  const [doNotTranslate, setDoNotTranslate] = useState(false);
  const [transliteration, setTransliteration] = useState('');
  const [note, setNote] = useState('');

  const chosen = languages.find((l) => l.code === language);
  const ready = term.trim() !== '' && (doNotTranslate || rendering.trim() !== '');

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onAgree({
          language,
          termSource: term.trim(),
          preferredRendering: rendering.trim(),
          doNotTranslate,
          transliteration: transliteration.trim(),
          note: note.trim(),
        });
      }}
      className="mb-5 flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
    >
      <h2 className="text-sm font-semibold">Agree a rendering</h2>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">The Arabic term</span>
          <input
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            lang="ar"
            dir="rtl"
            placeholder="التَّقْوَى"
            className="script-ar h-11 rounded-md border border-border bg-background px-3 text-lg outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">Language</span>
          <select
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            className="h-11 rounded-md border border-border bg-background px-2"
          >
            {languages.map((entry) => (
              <option key={entry.code} value={entry.code}>
                {entry.nameEn}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={doNotTranslate}
          onChange={(event) => setDoNotTranslate(event.target.checked)}
          className="mt-0.5"
        />
        <span>
          Leave it in Arabic
          <span className="block text-xs text-muted-foreground">
            Some words lose more in translation than they gain. Recording that
            decision here keeps every translator making it the same way.
          </span>
        </span>
      </label>

      {!doNotTranslate ? (
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">
            How it should read in {chosen?.nameEn ?? language}
          </span>
          <input
            value={rendering}
            onChange={(event) => setRendering(event.target.value)}
            dir={chosen?.direction ?? 'ltr'}
            className={cn(
              chosen?.scriptClass ?? 'script-latin',
              'h-11 rounded-md border border-border bg-background px-3 outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
            )}
          />
        </label>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">Transliteration</span>
          <input
            value={transliteration}
            onChange={(event) => setTransliteration(event.target.value)}
            placeholder="taqwā"
            className="h-11 rounded-md border border-border bg-background px-3"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">When to use it</span>
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className="h-11 rounded-md border border-border bg-background px-3"
          />
        </label>
      </div>

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Could not save it.'}
        </p>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="lg" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="lg" disabled={!ready || pending}>
          {pending ? 'Saving…' : 'Agree'}
        </Button>
      </div>
    </form>
  );
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-8 text-center">
      <p className="font-semibold">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-pretty text-muted-foreground">
        {body}
      </p>
    </div>
  );
}

function Failure({ error }: { error: unknown }) {
  const api = error instanceof ApiError ? error : null;
  return (
    <div role="alert" className="rounded-lg border border-destructive p-4">
      <p className="font-semibold">
        {api?.unauthenticated ? 'Sign in to the editor surface first' : 'Could not load'}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {error instanceof Error ? error.message : 'Something went wrong.'}
      </p>
    </div>
  );
}
